import { useEffect, useMemo, useState, useRef } from 'react';
import { Typography, Switch, FormControlLabel, TextField, Checkbox, MenuItem, Alert } from '@mui/material';
import { Grid, FormGroup, Button, DialogContent, DialogTitle, Chip, Collapse } from '@mui/material';
import { RoundedDialog, StandardDialogActions, TimePicker, UserChip, GroupChip, PortalGroupChip } from '../Components';
import { RichTextField } from '../Components';
import { NotificationsActiveRounded, PersonAddRounded, AddCircleOutlineRounded } from '@mui/icons-material';
import { ErrorOutlineRounded, PriorityHighRounded, AccessTimeRounded } from '@mui/icons-material';
import { useDebounce } from '../hooks';
import { amber, grey, red } from '@mui/material/colors';
import StateManager from '../StateManager';
import { isEmpty, orderBy, isFunction, isArray, isEqual } from 'lodash';
import moment from 'moment';
import { v4 } from 'uuid';

const sources = {
  task: 'task',
  'form-entry': 'form',
  'process-entry': 'process',
  'process-entry-step': 'process',
  audit: 'audit',
};

const standardDueSoonSubjects = {
  task: 'Reminder - task is due in 7 days',
  audit: 'Reminder - audit is due in 7 days',
  'form-entry': 'Reminder - entry is due in 7 days',
  'process-entry': 'Reminder - process is due in 7 days',
  'process-entry-step': 'Reminder - step is due in 7 days',
};

const standardOverdueSubjects = {
  task: 'Reminder - task is overdue by 7 days',
  audit: 'Reminder - audit is overdue by 7 days',
  'form-entry': 'Reminder - entry is overdue by 7 days',
  'process-entry': 'Reminder - process is overdue by 7 days',
  'process-entry-step': 'Reminder - step is overdue by 7 days',
};

const standardEscalationSubjects = {
  task: `Overdue: 'Task title'`,
  audit: `Overdue: 'Audit title'`,
  'form-entry': `Overdue: 'Form title'`,
  'process-entry': `Overdue: 'Process title'`,
  'process-entry-step': `Overdue: 'Step title'`,
};

const defaultUpcomingSettings = () => ({
  occurrences: [],
  message: '',
  subject: '',
});

const defaultOverdueSettings = () => ({
  occurrences: [],
  message: '',
  subject: '',
  sendRepetitively: false,
  interval: 1,
});

const defaultEscalationSettings = () => ({
  sendToOwner: false,
  users: [],
  groups: [],
  portalGroups: [],
  occurrences: [],
  message: '',
  subject: '',
});

const defaultDueTime = () =>
  moment().add(7, 'days').set({ hours: 17, minutes: 0, seconds: 0, milliseconds: 0 }).toDate();

const renderReminderOccurrences = (occurrences, onDelete) => {
  if (!isArray(occurrences) || isEmpty(occurrences)) {
    return (
      <Typography color={grey[600]} gutterBottom>
        No dates set
      </Typography>
    );
  }

  const ordered = [
    ...orderBy(
      occurrences.filter((x) => x.unit === 'week'),
      'value',
      'desc',
    ),
    ...orderBy(
      occurrences.filter((x) => x.unit === 'day'),
      'value',
      'desc',
    ),
    ...orderBy(
      occurrences.filter((x) => x.unit === 'hour'),
      'value',
      'desc',
    ),
  ];

  return ordered.map((item) => (
    <Chip
      key={item.id}
      label={`${item.value} ${item.unit}${item.value === 1 ? '' : 's'}`}
      sx={{ mr: 2, mb: 1 }}
      onDelete={() => onDelete(item.id)}
      color="primary"
    />
  ));
};

const ReminderSettings = ({ sourceType, initial, onChange, defaultEnabled, debounceMs = 250 }) => {
  const [enabled, setEnabled] = useState(false);

  const [upcomingDayDialogOpen, setUpcomingDayDialogOpen] = useState(false);
  const [customizeUpcoming, setCustomizeUpcoming] = useState(false);
  const [upcomingSettings, setUpcomingSettings] = useState(defaultUpcomingSettings());

  const [overdueDayDialogOpen, setOverdueDayDialogOpen] = useState(false);
  const [customizeOverdue, setCustomizeOverdue] = useState(false);
  const [overdueSettings, setOverdueSettings] = useState(defaultOverdueSettings());

  const [escalationDayDialogOpen, setEscalationDayDialogOpen] = useState(false);
  const [customizeEscalation, setCustomizeEscalation] = useState(false);
  const [escalationSettings, setEscalationSettings] = useState(defaultEscalationSettings());

  const [reminderTime, setReminderTime] = useState(defaultDueTime());

  const prevResult = useRef(null);

  const result = useMemo(() => {
    if (!enabled) return null;

    const result = {
      upcomingSettings,
      overdueSettings,
      escalationSettings,
      reminderTime,
    };

    return result;
  }, [enabled, upcomingSettings, overdueSettings, escalationSettings, reminderTime]);

  const debouncedResult = useDebounce(result, debounceMs);

  useEffect(() => {
    if (!isFunction(onChange)) return;
    if (isEqual(prevResult.current, debouncedResult)) {
      return;
    }
    onChange(debouncedResult);
    prevResult.current = debouncedResult;
  }, [debouncedResult, onChange]);

  const addDayBefore = (res) => {
    if (upcomingSettings.occurrences.some((x) => x.value === res.value && x.unit === res.unit)) return;
    setUpcomingSettings({ ...upcomingSettings, occurrences: [...upcomingSettings.occurrences, res] });
  };

  const deleteDayBefore = (id) => {
    setUpcomingSettings({
      ...upcomingSettings,
      occurrences: upcomingSettings.occurrences.filter((x) => x.id !== id),
    });
  };

  const addDayAfter = (res) => {
    if (overdueSettings.occurrences.some((x) => x.value === res.value && x.unit === res.unit)) return;
    setOverdueSettings({ ...overdueSettings, occurrences: [...overdueSettings.occurrences, res] });
  };

  const deleteDayAfter = (id) => {
    setOverdueSettings({ ...overdueSettings, occurrences: overdueSettings.occurrences.filter((x) => x.id !== id) });
  };

  const addEscalationDay = (res) => {
    if (escalationSettings.occurrences.some((x) => x.value === res.value && x.unit === res.unit)) return;
    setEscalationSettings({ ...escalationSettings, occurrences: [...escalationSettings.occurrences, res] });
  };

  const deleteEscalationDay = (id) => {
    setEscalationSettings({
      ...escalationSettings,
      occurrences: escalationSettings.occurrences.filter((x) => x.id !== id),
    });
  };

  const selectEscalationUsers = () => {
    StateManager.selectMultipleUsers(
      (res) => {
        let result = {};
        if (res.users) {
          result.users = res.users;
        }
        if (res.groups) {
          result.groups = res.groups;
        }
        if (res.portalGroups) {
          result.portalGroups = res.portalGroups;
        }
        setEscalationSettings({ ...escalationSettings, ...result });
      },
      {
        initiallySelected: escalationSettings?.users,
        initiallySelectedGroups: escalationSettings?.groups,
        initiallySelectedPortalGroups: escalationSettings?.portalGroups,
        showGroups: true,
        withoutPortal: true,
        showPortalGroups: true,
      },
    );
  };

  useEffect(() => {
    if (initial) {
      setEnabled(true);
      setUpcomingSettings(initial.upcomingSettings ?? defaultUpcomingSettings());
      setOverdueSettings(initial.overdueSettings ?? defaultOverdueSettings());
      setEscalationSettings(initial.escalationSettings ?? defaultEscalationSettings());
      setReminderTime(initial.reminderTime ?? defaultDueTime());
    } else {
      setEnabled(Boolean(defaultEnabled));
      setUpcomingSettings(defaultUpcomingSettings());
      setOverdueSettings(defaultOverdueSettings());
      setEscalationSettings(defaultEscalationSettings());
      setReminderTime(defaultDueTime());
    }
  }, [initial, defaultEnabled]);

  return (
    <Grid container>
      {!defaultEnabled && (
        <Grid container>
          <FormControlLabel
            control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
            label={'Enable reminders'}
          />
        </Grid>
      )}

      <Alert severity="info" sx={{ mt: 1 }}>
        <Typography variant="body2">
          Using the reminder setting, you can create reminders before a task is due, reminders when a task is overdue,
          and escalate by sending notification to people
        </Typography>
      </Alert>

      <Collapse in={enabled}>
        <Grid container sx={{ mt: 2 }}>
          <Grid container alignItems={'center'} sx={{ mb: 1 }}>
            <NotificationsActiveRounded fontSize="large" sx={{ color: grey[500] }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 500, ml: 1 }}>
              Create reminders for people before it's due
            </Typography>
          </Grid>
          <Grid container>
            <Typography gutterBottom>
              Click the add reminder button and select the time before the due date the reminder message should be sent
            </Typography>
          </Grid>
          <Grid container sx={{ mt: 1 }}>
            {renderReminderOccurrences(upcomingSettings.occurrences, (id) => deleteDayBefore(id))}
          </Grid>
          <Grid container sx={{ mt: 1 }}>
            <Button
              startIcon={<AddCircleOutlineRounded />}
              variant="outlined"
              onClick={(e) => setUpcomingDayDialogOpen(true)}
            >
              Add reminder
            </Button>

            <OccurenceDialog
              open={upcomingDayDialogOpen}
              onClose={() => setUpcomingDayDialogOpen(false)}
              onResult={addDayBefore}
            />
          </Grid>
          <Grid container sx={{ mt: 2 }}>
            <Typography sx={{ fontWeight: 500 }}>
              A reminder email will be sent along with an app notification
            </Typography>
          </Grid>

          <Grid container sx={{ mt: 1 }}>
            <Typography gutterBottom>
              If you want to, you can customise the email subject line and body. The standard subject will be like{' '}
              <i>'{standardDueSoonSubjects[sourceType] ?? ''}'</i>
            </Typography>
          </Grid>
          <Grid container sx={{ mt: 1 }}>
            <FormControlLabel
              control={<Switch checked={customizeUpcoming} onChange={(e) => setCustomizeUpcoming(e.target.checked)} />}
              label={'Customize email'}
            />
          </Grid>
          <Grid container component={Collapse} in={customizeUpcoming}>
            <Grid container sx={{ mt: 1 }}>
              <TextField
                label="Email subject"
                variant="outlined"
                value={upcomingSettings.subject}
                onChange={(e) => setUpcomingSettings({ ...upcomingSettings, subject: e.target.value })}
                fullWidth
              />
            </Grid>

            <Grid container sx={{ my: 1 }}>
              <Typography>Should there be a message in the reminder email?</Typography>
            </Grid>

            <Grid container>
              <RichTextField
                initial={upcomingSettings.message}
                placeholder={'Message (optional)'}
                editable
                editOpen
                hideButton
                onChange={(message) => setUpcomingSettings({ ...upcomingSettings, message })}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid container sx={{ mt: 2 }}>
          <Grid container alignItems={'center'} sx={{ mb: 1 }}>
            <ErrorOutlineRounded fontSize="large" sx={{ color: amber[500] }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 500, ml: 1 }}>
              Create reminders for people after it's overdue
            </Typography>
          </Grid>
          <Grid container>
            <Typography gutterBottom>
              Click the add reminder button and select the time after the due date the reminder message should be sent
            </Typography>
          </Grid>

          <Grid container sx={{ mt: 1 }}>
            {renderReminderOccurrences(overdueSettings.occurrences, (id) => deleteDayAfter(id))}
          </Grid>

          <Grid container sx={{ mt: 1 }}>
            <Button
              startIcon={<AddCircleOutlineRounded />}
              variant="outlined"
              onClick={(e) => setOverdueDayDialogOpen(true)}
            >
              Add reminder
            </Button>

            <OccurenceDialog
              open={overdueDayDialogOpen}
              onClose={() => setOverdueDayDialogOpen(false)}
              onResult={addDayAfter}
              forOverdue
            />
          </Grid>

          <Grid container alignItems="center" sx={{ mt: 1 }}>
            <FormControlLabel
              control={<Checkbox checked={overdueSettings.sendRepetitively} />}
              onChange={(e) => setOverdueSettings({ ...overdueSettings, sendRepetitively: e.target.checked })}
              label="Send repetitive reminders every"
            />
            <TextField
              placeholder="x"
              disabled={!overdueSettings.sendRepetitively}
              value={overdueSettings.interval}
              onChange={(e) => {
                const value = e.target.value === '' ? '' : Number(e.target.value);
                if (value !== '' && value <= 0) return;
                setOverdueSettings({ ...overdueSettings, interval: value });
              }}
              sx={{ width: 80, mx: 1 }}
              autoFocus
              autoComplete="off"
              variant="standard"
              type="number"
            />
            <Typography>days</Typography>
          </Grid>

          <Grid container sx={{ mt: 2 }}>
            <Typography sx={{ fontWeight: 500 }}>
              A reminder email will be sent along with an app notification
            </Typography>
          </Grid>

          <Grid container sx={{ mt: 1 }}>
            <Typography gutterBottom>
              Do you want to customize the reminder email subject? The standard subject will be like{' '}
              <i>'{standardOverdueSubjects[sourceType] ?? ''}'</i>
            </Typography>
          </Grid>

          <Grid container sx={{ mt: 1 }}>
            <FormControlLabel
              control={<Switch checked={customizeOverdue} onChange={(e) => setCustomizeOverdue(e.target.checked)} />}
              label={'Customize email'}
            />
          </Grid>

          <Grid container component={Collapse} in={customizeOverdue}>
            <Grid container sx={{ mt: 1 }}>
              <TextField
                label="Email subject"
                variant="outlined"
                value={overdueSettings.subject}
                onChange={(e) => setOverdueSettings({ ...overdueSettings, subject: e.target.value })}
                fullWidth
              />
            </Grid>

            <Grid container sx={{ my: 1 }}>
              <Typography>Should there be a message in the reminder email?</Typography>
            </Grid>

            <Grid container>
              <RichTextField
                initial={overdueSettings.message}
                placeholder={'Message (optional)'}
                editable
                editOpen
                hideButton
                onChange={(message) => setOverdueSettings({ ...overdueSettings, message })}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid container sx={{ mt: 2 }}>
          <Grid container sx={{ mb: 1 }}>
            <PriorityHighRounded fontSize="large" sx={{ color: red[500] }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 500, ml: 1 }}>
              Escalation
            </Typography>
          </Grid>

          <Grid container>
            <Typography gutterBottom>
              Click the add date button and select the time after the due date the escalation message should be sent
            </Typography>
          </Grid>

          <Grid container sx={{ mt: 1 }}>
            {renderReminderOccurrences(escalationSettings.occurrences, (id) => deleteEscalationDay(id))}
          </Grid>

          <Grid container sx={{ mt: 1 }}>
            <Button
              startIcon={<AddCircleOutlineRounded />}
              variant="outlined"
              onClick={(e) => setEscalationDayDialogOpen(true)}
            >
              Add date
            </Button>

            <OccurenceDialog
              open={escalationDayDialogOpen}
              onClose={() => setEscalationDayDialogOpen(false)}
              onResult={addEscalationDay}
              forEscalation
              forOverdue
            />
          </Grid>

          <Grid container item>
            <Grid container item alignItems="center" sx={{ py: 1 }}>
              <Grid container item>
                <Typography sx={{ fontWeight: 500 }} gutterBottom>
                  Who should receive the escalation message?
                </Typography>
              </Grid>
              <Button
                startIcon={<PersonAddRounded />}
                onClick={selectEscalationUsers}
                sx={{ mt: 1, mr: 2 }}
                variant="outlined"
              >
                Add users and groups
              </Button>
              {isArray(escalationSettings?.users) &&
                escalationSettings.users.map((user) => (
                  <UserChip
                    key={user}
                    id={user}
                    onDelete={() =>
                      setEscalationSettings({
                        ...escalationSettings,
                        users: escalationSettings.users.filter((x) => x !== user),
                      })
                    }
                  />
                ))}
              {isArray(escalationSettings?.groups) &&
                escalationSettings.groups.map((id) => (
                  <GroupChip
                    key={id}
                    id={id}
                    onDelete={() =>
                      setEscalationSettings({
                        ...escalationSettings,
                        groups: escalationSettings.groups.filter((x) => x !== id),
                      })
                    }
                  />
                ))}
              {isArray(escalationSettings?.portalGroups) &&
                escalationSettings.portalGroups.map((id) => (
                  <PortalGroupChip
                    key={id}
                    id={id}
                    onDelete={() =>
                      setEscalationSettings({
                        ...escalationSettings,
                        portalGroups: escalationSettings.portalGroups.filter((x) => x !== id),
                      })
                    }
                  />
                ))}
            </Grid>
            {sources[sourceType] && (
              <FormGroup sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={escalationSettings.sendToOwner}
                      onChange={(event) => {
                        setEscalationSettings({ ...escalationSettings, sendToOwner: event.target.checked });
                      }}
                      name="sendToOwner"
                    />
                  }
                  label={`Send to ${sources[sourceType] ?? ''} owner`}
                />
              </FormGroup>
            )}
          </Grid>

          <Grid container sx={{ mt: 2 }}>
            <Typography sx={{ fontWeight: 500 }}>
              Escalation email will be sent along with notification in the app
            </Typography>
          </Grid>

          <Grid container sx={{ mt: 2 }}>
            <Typography gutterBottom>
              If you want to, you can customise the email subject line and body. The standard subject will be like{' '}
              <i>'{standardEscalationSubjects[sourceType] ?? ''}'</i>
            </Typography>
          </Grid>

          <Grid container sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Switch checked={customizeEscalation} onChange={(e) => setCustomizeEscalation(e.target.checked)} />
              }
              label={'Customize email'}
            />
          </Grid>

          <Grid container component={Collapse} in={customizeEscalation}>
            <Grid container sx={{ mt: 1 }}>
              <TextField
                label="Email subject"
                variant="outlined"
                value={escalationSettings.subject}
                onChange={(e) => setEscalationSettings({ ...escalationSettings, subject: e.target.value })}
                fullWidth
              />
            </Grid>

            <Grid container sx={{ my: 1 }}>
              <Typography>Should there be a message in the reminder email?</Typography>
            </Grid>

            <Grid container>
              <RichTextField
                initial={escalationSettings.message}
                placeholder={'Message (optional)'}
                editable
                editOpen
                hideButton
                onChange={(message) => setEscalationSettings({ ...escalationSettings, message })}
              />
            </Grid>
          </Grid>
        </Grid>

        {false && (
          <Grid container sx={{ mt: 2 }}>
            <Grid container alignItems={'center'} sx={{ mb: 1 }}>
              <AccessTimeRounded fontSize="large" sx={{ color: grey[500] }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 500, ml: 1 }}>
                Lastly, what time of the day should the reminders be sent?
              </Typography>
            </Grid>
            <TimePicker value={reminderTime} onChange={(e) => setReminderTime({ ...upcomingSettings, dueTime: e })} />
          </Grid>
        )}
      </Collapse>
    </Grid>
  );
};

const OCCURENCE_UNITS = ['week', 'day', 'hour'];

const OccurenceDialog = ({ open, onClose, onResult, forEscalation, forOverdue }) => {
  const [daysInput, setDaysInput] = useState(7);
  const [selectedUnit, setSelectedUnit] = useState('day');

  useEffect(() => {
    setDaysInput(7);
    setSelectedUnit('day');
  }, [open]);

  function done() {
    onResult({ unit: selectedUnit, value: daysInput, id: v4() });
    onClose();
  }

  return (
    <RoundedDialog open={open} onClose={onClose}>
      <DialogTitle>{forEscalation ? 'Add escalation notification' : 'Add reminder message'}</DialogTitle>
      <DialogContent>
        <Grid container item alignItems="end" style={{ marginTop: '1rem' }}>
          <Grid container>
            <Typography>{forEscalation ? 'Send escalation' : 'Send reminder'}</Typography>
            <TextField
              placeholder="x"
              value={daysInput}
              onChange={(e) => {
                const value = e.target.value === '' ? '' : Number(e.target.value);
                if (value !== '' && value <= 0) return;
                setDaysInput(value);
              }}
              sx={{ width: 80, mx: 1 }}
              autoFocus
              autoComplete="off"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  done();
                }
              }}
              variant="standard"
              type="number"
            />
            <TextField
              sx={{ width: 120, mx: 1 }}
              select
              value={selectedUnit}
              onChange={(e) => setSelectedUnit(e.target.value)}
              variant="standard"
            >
              {OCCURENCE_UNITS.map((unit) => (
                <MenuItem key={unit} value={unit}>
                  {unit}
                  {daysInput === 1 ? '' : 's'}
                </MenuItem>
              ))}
            </TextField>
            <Typography>{forOverdue ? 'after' : 'before'} due date</Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <StandardDialogActions onClose={onClose} onDone={done} />
    </RoundedDialog>
  );
};

export default ReminderSettings;
